/**
 * Why picked storage entries were not attached, in the user's language.
 *
 * tri-server's `…/context-documents/from-storage` answers `skippedDetails: [{ name, reason }]` —
 * on a partial success and on the `NOTHING_ATTACHABLE` refusal alike. One sentence per reason, each
 * naming the files and what to do instead; an unknown reason (a newer server) falls back to the
 * generic sentence rather than disappearing.
 */

export type StorageAttachSkipReason =
    | 'archive'
    | 'unsupported_type'
    | 'not_found'
    | 'no_access'
    | 'registration_failed'
    | 'processing_unavailable';

export interface StorageAttachSkippedEntry {
    name: string;
    reason: string;
}

const KNOWN_REASONS: StorageAttachSkipReason[] = [
    'archive',
    'unsupported_type',
    'not_found',
    'no_access',
    'registration_failed',
    'processing_unavailable',
];

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** At most three names, then "(+n)". */
export const listSkippedNames = (names: string[]): string =>
    names.length > 3 ? `${names.slice(0, 3).join(', ')} (+${names.length - 3})` : names.join(', ');

/** Keeps only well-formed entries — the field comes off the wire. */
export const readSkippedDetails = (raw: unknown): StorageAttachSkippedEntry[] =>
    Array.isArray(raw)
        ? raw.filter(
              (entry: any): entry is StorageAttachSkippedEntry =>
                  typeof entry?.name === 'string' &&
                  entry.name.length > 0 &&
                  typeof entry?.reason === 'string'
          )
        : [];

/**
 * The message for skipped entries; `nothingAttached` prefixes the sentence saying the whole pick
 * failed. Returns `null` when there is nothing to say.
 */
export const describeStorageAttachSkips = (
    t: Translate,
    details: StorageAttachSkippedEntry[],
    nothingAttached = false
): string | null => {
    const byReason = new Map<string, string[]>();
    for (const entry of details) {
        const reason = KNOWN_REASONS.includes(entry.reason as StorageAttachSkipReason)
            ? entry.reason
            : 'unknown';
        byReason.set(reason, [...(byReason.get(reason) ?? []), entry.name]);
    }
    const sentences = Array.from(byReason.entries()).map(([reason, names]) =>
        t(`chatExchange.storagePickSkip.${reason}`, { files: listSkippedNames(names) })
    );
    if (nothingAttached) {
        return [
            t('chatExchange.storagePickNothingAttached'),
            ...(sentences.length > 0 ? sentences : [t('chatExchange.storagePickSkip.empty')]),
        ].join(' ');
    }
    return sentences.length > 0 ? sentences.join(' ') : null;
};
